import { createBrowserRouter } from "react-router-dom";
import AuthLayout from "./layouts/AuthLayout";
import AppLayout from "./layouts/AppLayout";
import AdminLayout from "./layouts/AdminLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import MasterProtectedRoute from "./components/MasterProtectedRoute";
import MasterRoute from "./components/MasterRoute";
import { RoleThemeProvider } from "./context/RoleThemeContext";

import Login from "./pages/auth/Login";
import AuthOrDemoEntry from "./pages/auth/AuthOrDemoEntry";
import DemoEnded from "./pages/auth/DemoEnded";
import CompanyAuthGate from "./pages/auth/CompanyAuthGate";
import Register from "./pages/auth/Register";
import SalesRepRegister from "./pages/auth/SalesRepRegister";
import RegisterSuccess from "./pages/auth/RegisterSuccess";
import ContactUs from "./pages/auth/ContactUs";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ForgotUsername from "./pages/auth/ForgotUsername";
import ResetPassword from "./pages/auth/ResetPassword";
import Dashboard from "./pages/dashboard/Dashboard";
import Profile from "./pages/profile/Profile";

import Seller from "./pages/calculators/seller/";
import Buyer from "./pages/calculators/buyer/";
import PropNine from "./pages/calculators/propnine/";
import Refinance from "./pages/calculators/refinance/";
// import Refi from "./pages/calculators/refi/";
import Seller2net from "./pages/calculators/seller2net/";

import Open from "./pages/calculators/open/Open";
import Settings from "./pages/calculators/settings/Settings";
import SettingsPage from "./pages/calculators/settings/SettingsPage";

import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminMainCompany from "./pages/admin/AdminMainCompany";
import AdminCompany from "./pages/admin/AdminCompany";
import AdminEmailTemplates from "./pages/admin/AdminEmailTemplates";
import AdminNotifyUsers from "./pages/admin/AdminNotifyUsers";
import AdminManageCMS from "./pages/admin/AdminManageCMS";
import AdminGlobalDefaultTemplate from "./pages/admin/AdminGlobalDefaultTemplate";
import AdminCompanySetting from "./pages/admin/AdminCompanySetting";
import AdminAdvertise from "./pages/admin/AdminAdvertise";
import TransferTitleRep from "./pages/admin/AdminTransferTitleRep";
import GlobalStateSetting from "./pages/admin/AdminGlobalStateSetting";
import AdminCountySetting from "./pages/admin/AdminCountySetting";
import NationalSetting from "./pages/admin/AdminNationalSetting";
import AdminSalesReps from "./pages/admin/AdminSalesReps";
import AdminUsersSalesRepsCombined from "./pages/admin/AdminUsersSalesRepsCombined";
import AdminMasterThemes from "./pages/admin/AdminMasterThemes";
import AdminTrackingSummary from "./pages/admin/AdminTrackingSummary";
import AdminDashboardOptions from "./pages/admin/AdminDashboardOptions";
import AdminManageDisclaimer from "./pages/admin/AdminManageDisclaimer";
import AdminChangePassword from "./pages/admin/AdminChangePassword";
import UpcomingTransaction from "./pages/upcomingTransaction";
import EstimedClosingLayout from "./layouts/EstimedClosingLayout";
import RouteErrorPage, { NotFoundPage } from "./components/common/RouteErrorPage";

export const router = createBrowserRouter([
  // Public/Auth routes
  {
    path: "/",
    element: <AuthLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      { path: "login", element: <Login /> },
      { path: "register", element: <Register /> },
      { path: "sales-rep-register", element: <SalesRepRegister /> },
      { path: "register/success", element: <RegisterSuccess /> },
      { path: "forgot-password", element: <ForgotPassword /> },
      { path: "reset-password", element: <ResetPassword /> },
      { path: "forgot-username", element: <ForgotUsername /> },
      { path: "contact", element: <ContactUs /> },
      { index: true, element: <AuthOrDemoEntry /> }, // default (or guest-demo entry on the demo host)
      { path: "demo-ended", element: <DemoEnded /> },
      { path: "transaction", element: <UpcomingTransaction /> },
      {
        path: ":landingCompanyName",
        element: <CompanyAuthGate />,
        children: [
          { index: true, element: <Login /> },
          { path: "login", element: <Login /> },
          { path: "register", element: <Register /> },
          { path: "sales-rep-register", element: <SalesRepRegister /> },
          { path: "register/success", element: <RegisterSuccess /> },
          { path: "forgot-password", element: <ForgotPassword /> },
          { path: "reset-password", element: <ResetPassword /> },
          { path: "forgot-username", element: <ForgotUsername /> },
          { path: "contact", element: <ContactUs /> },
        ],
      },
    ],
  },
  // User (authenticated)
  {
    path: "/",
    element: <ProtectedRoute />, // protect these
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: "dashboard", element: <Dashboard /> },
          { path: "profile", element: <Profile /> },
          { path: "seller", element: <Seller /> },
          { path: "seller/:id", element: <Seller /> },
          { path: "buyer", element: <Buyer /> },
          { path: "buyer/:id", element: <Buyer /> },
          { path: "propnine", element: <PropNine /> },
          { path: "refinance", element: <Refinance /> },
          { path: "refinance/:id", element: <Refinance /> },
          // { path: "refi", element: <Refi /> },
          // { path: "refi/:id", element: <Refi /> },
          { path: "seller2net", element: <Seller2net /> },
          { path: "seller2net/:id", element: <Seller2net /> },
          { path: "open", element: <Open /> },
          { path: "settings", element: <Settings /> },
          { path: "settings-page", element: <SettingsPage /> },
          { index: true, element: <Dashboard /> }, // default
          { path: "*", element: <NotFoundPage context="app" /> },
          // add more secured pages here
        ],
      },
    ],
  },
  // 🔹 Master routes (without /admin prefix) - for master users only
  {
    path: "/master",
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: "dashboard",
        element: (
          <RoleThemeProvider>
            <MasterRoute>
              <AdminLayout />
            </MasterRoute>
          </RoleThemeProvider>
        ),
        children: [{ index: true, element: <AdminDashboard /> }],
      },
      {
        path: "change-password",
        element: (
          <RoleThemeProvider>
            <MasterRoute>
              <AdminLayout />
            </MasterRoute>
          </RoleThemeProvider>
        ),
        children: [{ index: true, element: <AdminChangePassword /> }],
      },
      {
        path: "users",
        element: (
          <RoleThemeProvider>
            <MasterRoute>
              <AdminLayout />
            </MasterRoute>
          </RoleThemeProvider>
        ),
        children: [{ index: true, element: <AdminUsers /> }],
      },
      {
        path: "company-settings",
        element: (
          <RoleThemeProvider>
            <MasterRoute>
              <AdminLayout />
            </MasterRoute>
          </RoleThemeProvider>
        ),
        children: [
          { index: true, element: <AdminCompanySetting /> },
          {
            path: ":maincompanyid/:companyid",
            element: <AdminCompanySetting />,
          },
        ],
      },
      {
        path: "sales-reps",
        element: (
          <RoleThemeProvider>
            <MasterRoute>
              <AdminLayout />
            </MasterRoute>
          </RoleThemeProvider>
        ),
        children: [{ index: true, element: <AdminSalesReps /> }],
      },
      {
        path: "tracking-summary",
        element: (
          <RoleThemeProvider>
            <MasterRoute>
              <AdminLayout />
            </MasterRoute>
          </RoleThemeProvider>
        ),
        children: [{ index: true, element: <AdminTrackingSummary /> }],
      },
      {
        path: "notify-users",
        element: (
          <RoleThemeProvider>
            <MasterRoute>
              <AdminLayout />
            </MasterRoute>
          </RoleThemeProvider>
        ),
        children: [{ index: true, element: <AdminNotifyUsers /> }],
      },
      {
        path: "dashboard-options",
        element: (
          <RoleThemeProvider>
            <MasterRoute>
              <AdminLayout />
            </MasterRoute>
          </RoleThemeProvider>
        ),
        children: [{ index: true, element: <AdminDashboardOptions /> }],
      },
      {
        path: "disclaimer",
        element: (
          <RoleThemeProvider>
            <MasterRoute>
              <AdminLayout />
            </MasterRoute>
          </RoleThemeProvider>
        ),
        children: [{ index: true, element: <AdminManageDisclaimer /> }],
      },
      {
        path: "cms",
        element: (
          <RoleThemeProvider>
            <MasterRoute>
              <AdminLayout />
            </MasterRoute>
          </RoleThemeProvider>
        ),
        children: [{ index: true, element: <AdminManageCMS /> }],
      },
      { path: "*", element: <NotFoundPage context="master" /> },
    ],
  },
  // 🔹 Admin
  {
    path: "/admin",
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <AdminLogin /> }, // default
      { path: "login", element: <AdminLogin /> },
      {
        element: (
          <RoleThemeProvider>
            <AdminProtectedRoute />
          </RoleThemeProvider>
        ),
        children: [
          {
            element: <AdminLayout />,
            children: [
              { path: "dashboard", element: <AdminDashboard /> },
              { path: "change-password", element: <AdminChangePassword /> },
              { path: "users", element: <AdminUsers /> },
              { path: "tracking-summary", element: <AdminTrackingSummary /> },
              {
                path: "main-company",
                element: (
                  <MasterProtectedRoute>
                    <AdminMainCompany />
                  </MasterProtectedRoute>
                ),
              },
              { path: "company/:id", element: <AdminCompany /> },
              {
                path: "email-templates",
                element: (
                  <MasterProtectedRoute>
                    <AdminEmailTemplates />
                  </MasterProtectedRoute>
                ),
              },
              {
                path: "notify-users",
                element: (
                  <MasterProtectedRoute>
                    <AdminNotifyUsers />
                  </MasterProtectedRoute>
                ),
              },
              {
                path: "cms",
                element: (
                  <MasterProtectedRoute>
                    <AdminManageCMS />
                  </MasterProtectedRoute>
                ),
              },
              {
                path: "global-template",
                element: (
                  <MasterProtectedRoute>
                    <AdminGlobalDefaultTemplate />
                  </MasterProtectedRoute>
                ),
              },
              {
                path: "company-settings",
                element: <AdminCompanySetting />,
              },
              {
                path: "company-settings/:maincompanyid/:companyid",
                element: <AdminCompanySetting />,
              },
              {
                path: "advertise",
                element: (
                  <MasterProtectedRoute>
                    <AdminAdvertise />
                  </MasterProtectedRoute>
                ),
              },
              {
                path: "transfer-titlerep",
                element: (
                  <MasterProtectedRoute>
                    <TransferTitleRep />
                  </MasterProtectedRoute>
                ),
              },
              {
                path: "state-settings",
                element: (
                  <MasterProtectedRoute>
                    <GlobalStateSetting />
                  </MasterProtectedRoute>
                ),
              },
              {
                path: "county-settings",
                element: (
                  <MasterProtectedRoute>
                    <AdminCountySetting />
                  </MasterProtectedRoute>
                ),
              },
              {
                path: "national-settings",
                element: (
                  <MasterProtectedRoute>
                    <NationalSetting />
                  </MasterProtectedRoute>
                ),
              },
              {
                path: "sales-reps",
                element: <AdminSalesReps />,
              },
              {
                path: "accounts",
                element: <AdminUsersSalesRepsCombined />,
              },
              {
                path: "dashboard-options",
                element: (
                  <MasterProtectedRoute>
                    <AdminDashboardOptions />
                  </MasterProtectedRoute>
                ),
              },
              {
                path: "disclaimer",
                element: (
                  <MasterProtectedRoute>
                    <AdminManageDisclaimer />
                  </MasterProtectedRoute>
                ),
              },
              {
                path: "themes/master",
                element: (
                  <MasterProtectedRoute>
                    <AdminMasterThemes />
                  </MasterProtectedRoute>
                ),
              },
              { path: "*", element: <NotFoundPage context="admin" /> },
              // add more admin pages here
            ],
          },
        ],
      },
    ],
  },
  // Not Logged in routes
  {
    path: "/",
    errorElement: <RouteErrorPage />,
    children: [
      { path: "estimated-closing/:tokenId", element: <EstimedClosingLayout /> },
    ],
  },
  { path: "*", element: <NotFoundPage context="app" /> },
]);
